import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import './Auth.css';


import { API } from '../config';

export default function MemberSignup() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const searchEmails = async () => {
      if (email.length < 2) {
        setSuggestions([]);
        return;
      }

      try {
        const response = await axios.get(`${API}/members/search-email?q=${email}`);
        setSuggestions(response.data);
        setShowSuggestions(true);
      } catch (error) {
        setSuggestions([]);
      }
    };

    const debounce = setTimeout(searchEmails, 300);
    return () => clearTimeout(debounce);
  }, [email]);

  const selectEmail = (suggestion) => {
    setEmail(suggestion.email);
    setSelectedMember(suggestion);
    setShowSuggestions(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedMember) {
      toast.error('Please select your email from the suggestions. You must be pre-registered by your group leadership.');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/members/complete-signup`, {
        email: selectedMember.email,
        password: password
      });
      
      toast.success('Account created successfully! You can now login.');
      setTimeout(() => navigate('/login?type=member'), 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page" data-testid="member-signup-page">
      <div className="auth-container">
        <div className="auth-card card">
          <div className="auth-header">
            <h1>Complete Your Registration</h1>
            <p>Set your password to access your group</p>
          </div>

          <div className="info-box">
            <p><strong>Note:</strong> Your email must be pre-registered by your group leadership. Start typing to see available options.</p>
          </div>

          <form onSubmit={handleSubmit} data-testid="member-signup-form">
            <div className="form-group">
              <label>Email Address</label>
              <div className="autocomplete-wrapper">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setSelectedMember(null);
                  }}
                  onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                  required
                  placeholder="Start typing your email..."
                  data-testid="email-input"
                />
                {showSuggestions && suggestions.length > 0 && (
                  <div className="suggestions-dropdown" data-testid="email-suggestions">
                    {suggestions.map((suggestion, index) => (
                      <div
                        key={index}
                        className="suggestion-item"
                        onClick={() => selectEmail(suggestion)}
                        data-testid={`suggestion-${index}`}
                      >
                        <div className="suggestion-name">{suggestion.name}</div>
                        <div className="suggestion-email">{suggestion.email}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {selectedMember && (
                <div className="selected-member">
                  ✓ Selected: {selectedMember.name}
                </div>
              )}
            </div>

            <div className="form-group">
              <label>Create Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength="6"
                data-testid="password-input"
              />
            </div>

            <div className="form-group">
              <label>Confirm Password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength="6"
                data-testid="confirm-password-input"
              />
            </div>

            <button 
              type="submit" 
              className="btn btn-primary full-width" 
              disabled={loading || !selectedMember}
              data-testid="signup-submit-btn"
            >
              {loading ? 'Creating Account...' : 'Complete Registration'}
            </button>
          </form>

          <div className="auth-footer">
            Already have an account? <Link to="/login?type=member">Login here</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
